/**
 * Action Button Components
 *
 * Specialized buttons for common application actions with semantic meaning.
 * Each component automatically applies appropriate styling and behavior.
 *
 * Usage:
 *   <RunButton onClick={handleRun}>Run Campaign</RunButton>
 *   <AddButton onClick={handleAdd}>Add Item</AddButton>
 *   <EditButton onClick={handleEdit}>Edit</EditButton>
 *   <InviteButton onClick={handleInvite}>Invite Member</InviteButton>
 */

import * as React from 'react';
import { Button, ButtonProps } from './button';
import { cn } from '@/lib/utils';
import { Play, Plus, Edit, UserPlus, Send, Download, Upload, Copy, Check } from 'lucide-react';

interface BaseActionButtonProps extends Omit<ButtonProps, 'variant'> {
  /**
   * Show icon in button (default: true for small buttons, false for large)
   */
  showIcon?: boolean;
}

/**
 * RunButton - Execute/Start action
 * Used for: Run, Start, Execute, Launch
 */
export const RunButton = React.forwardRef<HTMLButtonElement, BaseActionButtonProps>(
  ({ className, children = 'Run', showIcon, size = 'default', ...props }, ref) => {
    const shouldShowIcon = showIcon ?? (size === 'sm' || size === 'default');

    return (
      <Button
        ref={ref}
        variant="primary"
        size={size}
        className={cn(
          'bg-[var(--mb-primary)] hover:bg-[var(--mb-primary-hover)] text-white shadow-[var(--mb-primary-shadow)]',
          className
        )}
        startIcon={shouldShowIcon ? <Play size={16} /> : undefined}
        {...props}
      >
        {children}
      </Button>
    );
  }
);
RunButton.displayName = 'RunButton';

/**
 * AddButton - Create/Add action
 * Used for: Add, Create, New
 */
export const AddButton = React.forwardRef<HTMLButtonElement, BaseActionButtonProps>(
  ({ className, children = 'Add', showIcon, size = 'default', ...props }, ref) => {
    const shouldShowIcon = showIcon ?? (size === 'sm' || size === 'default');

    return (
      <Button
        ref={ref}
        variant="primary"
        size={size}
        className={cn(
          'bg-[var(--mb-primary)] hover:bg-[var(--mb-primary-hover)] text-white shadow-[var(--mb-primary-shadow)]',
          className
        )}
        startIcon={shouldShowIcon ? <Plus size={16} /> : undefined}
        {...props}
      >
        {children}
      </Button>
    );
  }
);
AddButton.displayName = 'AddButton';

/**
 * EditButton - Edit action (outline)
 * Used for: Edit, Modify, Change
 */
const EditButton = React.forwardRef<HTMLButtonElement, BaseActionButtonProps>(
  ({ className, children = 'Edit', showIcon, size = 'default', ...props }, ref) => {
    const shouldShowIcon = showIcon ?? (size === 'sm' || size === 'default');

    return (
      <Button
        ref={ref}
        variant="outline"
        size={size}
        className={cn(
          'border-gray-300 hover:bg-gray-50 text-gray-700',
          className
        )}
        startIcon={shouldShowIcon ? <Edit size={16} /> : undefined}
        {...props}
      >
        {children}
      </Button>
    );
  }
);
EditButton.displayName = 'EditButton';

/**
 * InviteButton - Invite/Add user action
 * Used for: Invite, Add Member
 */
export const InviteButton = React.forwardRef<HTMLButtonElement, BaseActionButtonProps>(
  ({ className, children = 'Invite', showIcon, size = 'default', ...props }, ref) => {
    const shouldShowIcon = showIcon ?? (size === 'sm' || size === 'default');

    return (
      <Button
        ref={ref}
        variant="primary"
        size={size}
        className={cn(
          'bg-[var(--mb-primary)] hover:bg-[var(--mb-primary-hover)] text-white shadow-[var(--mb-primary-shadow)]',
          className
        )}
        startIcon={shouldShowIcon ? <UserPlus size={16} /> : undefined}
        {...props}
      >
        {children}
      </Button>
    );
  }
);
InviteButton.displayName = 'InviteButton';

/**
 * SendButton - Send/Submit action
 * Used for: Send, Submit, Share
 */
export const SendButton = React.forwardRef<HTMLButtonElement, BaseActionButtonProps>(
  ({ className, children = 'Send', showIcon, size = 'default', ...props }, ref) => {
    const shouldShowIcon = showIcon ?? (size === 'sm' || size === 'default');

    return (
      <Button
        ref={ref}
        variant="primary"
        size={size}
        className={cn(
          'bg-[var(--mb-primary)] hover:bg-[var(--mb-primary-hover)] text-white shadow-[var(--mb-primary-shadow)]',
          className
        )}
        startIcon={shouldShowIcon ? <Send size={16} /> : undefined}
        {...props}
      >
        {children}
      </Button>
    );
  }
);
SendButton.displayName = 'SendButton';

/**
 * DownloadButton - Download action (outline)
 * Used for: Download, Export
 */
const DownloadButton = React.forwardRef<HTMLButtonElement, BaseActionButtonProps>(
  ({ className, children = 'Download', showIcon, size = 'default', ...props }, ref) => {
    const shouldShowIcon = showIcon ?? (size === 'sm' || size === 'default');

    return (
      <Button
        ref={ref}
        variant="outline"
        size={size}
        className={cn(
          'border-gray-300 hover:bg-gray-50 text-gray-700',
          className
        )}
        startIcon={shouldShowIcon ? <Download size={16} /> : undefined}
        {...props}
      >
        {children}
      </Button>
    );
  }
);
DownloadButton.displayName = 'DownloadButton';

/**
 * UploadButton - Upload action (outline)
 * Used for: Upload, Import
 */
const UploadButton = React.forwardRef<HTMLButtonElement, BaseActionButtonProps>(
  ({ className, children = 'Upload', showIcon, size = 'default', ...props }, ref) => {
    const shouldShowIcon = showIcon ?? (size === 'sm' || size === 'default');

    return (
      <Button
        ref={ref}
        variant="outline"
        size={size}
        className={cn(
          'border-gray-300 hover:bg-gray-50 text-gray-700',
          className
        )}
        startIcon={shouldShowIcon ? <Upload size={16} /> : undefined}
        {...props}
      >
        {children}
      </Button>
    );
  }
);
UploadButton.displayName = 'UploadButton';

/**
 * CopyButton - Copy action (outline)
 * Used for: Copy, Duplicate
 */
const CopyButton = React.forwardRef<HTMLButtonElement, BaseActionButtonProps>(
  ({ className, children = 'Copy', showIcon, size = 'default', ...props }, ref) => {
    const shouldShowIcon = showIcon ?? (size === 'sm' || size === 'default');

    return (
      <Button
        ref={ref}
        variant="outline"
        size={size}
        className={cn(
          'border-gray-300 hover:bg-gray-50 text-gray-700',
          className
        )}
        startIcon={shouldShowIcon ? <Copy size={16} /> : undefined}
        {...props}
      >
        {children}
      </Button>
    );
  }
);
CopyButton.displayName = 'CopyButton';

/**
 * ConfirmButton - Confirmation action (green)
 * Used for: Confirm, Approve, Accept
 */
export const ConfirmButton = React.forwardRef<HTMLButtonElement, BaseActionButtonProps>(
  ({ className, children = 'Confirm', showIcon, size = 'default', ...props }, ref) => {
    const shouldShowIcon = showIcon ?? (size === 'sm' || size === 'default');

    return (
      <Button
        ref={ref}
        variant="success"
        size={size}
        className={cn(
          'bg-green-500 hover:bg-green-600 text-white shadow-sm',
          className
        )}
        startIcon={shouldShowIcon ? <Check size={16} /> : undefined}
        {...props}
      >
        {children}
      </Button>
    );
  }
);
ConfirmButton.displayName = 'ConfirmButton';
